import { useEffect, useRef, useState } from 'react';

import { Toggle } from '@opendoor/bricks-next';
import Cookies from 'js-cookie';

import { LanguageCategory_Language } from '__generated__/protobuf/journey/data/categories/language_category';

import { Locale, LOCALES } from 'components/helpers/types';
import { getAnonymousId, setCookie } from 'components/shared/trackers';

import { determinePreferredLocale } from 'helpers/getPreferredLocale';
import 'helpers/globals';
import { i18n } from 'helpers/i18n';
import { useObservability } from 'helpers/observability';

export const LanguageToggle = () => {
  const { trackJourneyEvent } = useObservability();
  const anonId = getAnonymousId();
  const localeCookie = Cookies.get('locale');
  const [defaultToSpanish, setDefaultToSpanish] = useState(localeCookie === LOCALES.SPANISH);
  const [loading, setLoading] = useState(!localeCookie);

  useEffect(() => {
    const loadLangPref = async () => {
      const preferredLocale = await determinePreferredLocale({
        localeFromCookie: localeCookie,
        anonId,
      });
      if (preferredLocale === LOCALES.SPANISH) {
        setDefaultToSpanish(true);
      }
      setLoading(false);
    };

    loadLangPref();
  }, [anonId, localeCookie]);

  useEffect(() => {
    if (localeCookie && i18n.language !== localeCookie) {
      i18n.changeLanguage(localeCookie);
    }
  }, [localeCookie]);

  // check for pending language toggle event and send it
  useEffect(() => {
    const toggledLanguage = localStorage.getItem('toggledLanguage');
    if (toggledLanguage) {
      const languageEvent = parseInt(toggledLanguage, 10) as LanguageCategory_Language;
      trackJourneyEvent('language', 'selectEvent', { language: languageEvent });
      localStorage.removeItem('toggledLanguage');
    }
  }, [trackJourneyEvent]);

  const hasInteracted = useRef(false);

  const handleToggle = (nextValue: boolean) => {
    if (!hasInteracted.current) {
      hasInteracted.current = true;
      return; // skip the first toggle caused by initial state
    }

    const selectedLocale: Locale = nextValue ? LOCALES.SPANISH : LOCALES.ENGLISH;
    i18n.changeLanguage(selectedLocale);
    setCookie('locale', selectedLocale, 365);

    const languageEvent =
      selectedLocale === LOCALES.SPANISH
        ? LanguageCategory_Language.SPANISH
        : LanguageCategory_Language.ENGLISH;

    // store the language event in localStorage to send after reload
    localStorage.setItem('toggledLanguage', languageEvent.toString());

    // reload so we invoke routing to language-specific landing page in Cloudflare
    window.location.reload();
  };

  if (loading) return null;

  return (
    <Toggle
      size="large"
      onCheckedChange={handleToggle}
      defaultChecked={defaultToSpanish}
      leftLabel="English"
      rightLabel="Spanish"
    />
  );
};
